import { useCallback, useEffect, useRef, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  ArrowLeft,
  ArrowRight,
  Boxes,
  Calendar,
  Check,
  Loader2,
  MapPin,
  Minus,
  Package,
  Plus,
  RefreshCw,
  X,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Separator } from "~/components/ui/separator";
import { useToast } from "~/hooks/use-toast";
import {
  useLookupPallet,
  useLookupPosition,
  useRelocatePallet,
} from "~/hooks/wms";
import { supabase } from "~/integrations/supabase/client";
import {
  parseBarcode,
  validateBarcodeType,
} from "~/services/wms/barcodeService";
import type { PalletWithDetails } from "~/services/wms/palletService";
import { useProfileStore } from "~/store/profileStore";
import type { Position, WmsInventory } from "~/types/wms";
import { PALLET_STATUS_LABELS, PALLET_TYPE_LABELS } from "~/types/wms";

type RelocationStep =
  | "select-type"
  | "scan-source"
  | "scan-pallet"
  | "select-inventory"
  | "view-pallet"
  | "scan-destination"
  | "confirm"
  | "completed";

type RelocationType = "inventory" | "pallet";

interface InventoryWithProduct extends WmsInventory {
  product?: { id: string; short_name: string };
  production_batch?: { batch_number: string; lot_number?: string };
}

export function RelocationPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const profileId = useProfileStore((s) => s.id);

  // Common state
  const [currentStep, setCurrentStep] = useState<RelocationStep>("select-type");
  const [relocationType, setRelocationType] = useState<RelocationType | null>(
    null
  );
  const [destinationPosition, setDestinationPosition] =
    useState<Position | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Inventory-specific state
  const [sourcePosition, setSourcePosition] = useState<Position | null>(null);
  const [selectedInventory, setSelectedInventory] =
    useState<InventoryWithProduct | null>(null);
  const [quantity, setQuantity] = useState(0);

  // Pallet-specific state
  const [pallet, setPallet] = useState<PalletWithDetails | null>(null);
  const [palletSourcePosition, setPalletSourcePosition] =
    useState<Position | null>(null);

  // Input state for barcode scanning
  const [sourceInput, setSourceInput] = useState("");
  const [palletInput, setPalletInput] = useState("");
  const [destInput, setDestInput] = useState("");

  // Refs for auto-focus
  const sourceInputRef = useRef<HTMLInputElement>(null);
  const palletInputRef = useRef<HTMLInputElement>(null);
  const destInputRef = useRef<HTMLInputElement>(null);

  // Pallet mutations
  const lookupPallet = useLookupPallet();
  const lookupPosition = useLookupPosition();
  const relocatePallet = useRelocatePallet();

  // Auto-focus based on step
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentStep === "scan-source") {
        sourceInputRef.current?.focus();
      } else if (currentStep === "scan-pallet") {
        palletInputRef.current?.focus();
      } else if (currentStep === "scan-destination") {
        destInputRef.current?.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [currentStep]);

  // Fetch inventory at source position (for inventory flow)
  const { data: sourceInventory, isLoading: loadingInventory } = useQuery({
    queryKey: ["position-inventory", sourcePosition?.id],
    queryFn: async () => {
      if (!sourcePosition?.id) return [];
      const { data, error } = await supabase
        .from("wms_inventory")
        .select(
          `
          *,
          product:products(id, short_name),
          production_batch:production_batches(batch_number)
        `
        )
        .eq("position_id", sourcePosition.id)
        .gt("box_count", 0);

      if (error) throw error;
      return data as InventoryWithProduct[];
    },
    enabled: !!sourcePosition?.id && relocationType === "inventory",
  });

  // Fetch suggested destination positions
  const { data: suggestedPositions } = useQuery({
    queryKey: ["suggested-positions", sourcePosition?.id, pallet?.id],
    queryFn: async () => {
      const excludeId =
        relocationType === "inventory"
          ? sourcePosition?.id
          : pallet?.position_id;
      const { data } = await supabase
        .from("wms_positions")
        .select("*")
        .eq("is_active", true)
        .neq("id", excludeId || "")
        .order("full_code", { ascending: true })
        .limit(5);
      return data as Position[];
    },
    enabled:
      currentStep === "scan-destination" &&
      (!!sourcePosition?.id || !!pallet?.id),
  });

  // Reset flow
  const resetFlow = useCallback(() => {
    setCurrentStep("select-type");
    setRelocationType(null);
    setSourcePosition(null);
    setSelectedInventory(null);
    setQuantity(0);
    setDestinationPosition(null);
    setPallet(null);
    setPalletSourcePosition(null);
    setSourceInput("");
    setPalletInput("");
    setDestInput("");
  }, []);

  // Handle type selection
  const handleTypeSelect = (type: RelocationType) => {
    setRelocationType(type);
    if (type === "inventory") {
      setCurrentStep("scan-source");
    } else {
      setCurrentStep("scan-pallet");
    }
  };

  // Handle position scan (for inventory flow)
  const handlePositionScan = useCallback(
    async (scannedValue: string, target: "source" | "destination") => {
      setIsProcessing(true);

      const validation = validateBarcodeType(scannedValue, "LOC");
      const positionCode = validation.valid
        ? validation.parsed.code
        : scannedValue;

      const { data: position, error } = await supabase
        .from("wms_positions")
        .select("*")
        .eq("full_code", positionCode)
        .single();

      setIsProcessing(false);

      if (error || !position) {
        toast({
          title: "Posición no encontrada",
          description: `No se encontró la posición: ${positionCode}`,
          variant: "destructive",
        });
        return;
      }

      if (target === "source") {
        setSourcePosition(position);
        setCurrentStep("select-inventory");
      } else {
        if (
          relocationType === "inventory" &&
          position.id === sourcePosition?.id
        ) {
          toast({
            title: "Posición inválida",
            description: "La posición destino debe ser diferente a la origen",
            variant: "destructive",
          });
          return;
        }
        if (
          relocationType === "pallet" &&
          position.id === pallet?.position_id
        ) {
          toast({
            title: "Posición inválida",
            description: "La posición destino debe ser diferente a la actual",
            variant: "destructive",
          });
          return;
        }
        setDestinationPosition(position);
        setCurrentStep("confirm");
      }
    },
    [sourcePosition, pallet, relocationType, toast]
  );

  // Handle pallet scan (for pallet flow)
  const handlePalletSubmit = useCallback(() => {
    if (!palletInput.trim()) return;

    const parsed = parseBarcode(palletInput.trim());
    let searchValue = palletInput.trim();

    if (parsed.type === "PALLET") {
      searchValue = parsed.code;
    }

    lookupPallet.mutate(searchValue, {
      onSuccess: (foundPallet) => {
        if (foundPallet) {
          setPallet(foundPallet);
          setPalletSourcePosition(foundPallet.position as Position | undefined);
          setCurrentStep("view-pallet");
          setPalletInput("");
        } else {
          toast({
            title: "Pallet no encontrado",
            description: `No se encontró el pallet: ${searchValue}`,
            variant: "destructive",
          });
          setPalletInput("");
          palletInputRef.current?.focus();
        }
      },
      onError: (err) => {
        toast({
          title: "Error",
          description:
            err instanceof Error ? err.message : "Error al buscar pallet",
          variant: "destructive",
        });
        setPalletInput("");
        palletInputRef.current?.focus();
      },
    });
  }, [palletInput, lookupPallet, toast]);

  // Handle destination position for pallet flow
  const handlePalletDestinationSubmit = useCallback(() => {
    if (!destInput.trim()) return;

    const parsed = parseBarcode(destInput.trim());
    let searchValue = destInput.trim();

    if (parsed.type === "LOC") {
      searchValue = parsed.code;
    }

    lookupPosition.mutate(searchValue, {
      onSuccess: (position) => {
        if (position) {
          if (position.id === pallet?.position_id) {
            toast({
              title: "Posición inválida",
              description: "La posición destino debe ser diferente a la actual",
              variant: "destructive",
            });
            setDestInput("");
            destInputRef.current?.focus();
            return;
          }
          setDestinationPosition(position);
          setCurrentStep("confirm");
          setDestInput("");
        } else {
          toast({
            title: "Posición no encontrada",
            description: `No se encontró la posición: ${searchValue}`,
            variant: "destructive",
          });
          setDestInput("");
          destInputRef.current?.focus();
        }
      },
      onError: (err) => {
        toast({
          title: "Error",
          description:
            err instanceof Error ? err.message : "Error al buscar posición",
          variant: "destructive",
        });
        setDestInput("");
        destInputRef.current?.focus();
      },
    });
  }, [destInput, pallet, lookupPosition, toast]);

  // Handle inventory selection
  const handleInventorySelect = (inventory: InventoryWithProduct) => {
    setSelectedInventory(inventory);
    const available = inventory.box_count - (inventory.reserved_boxes || 0);
    setQuantity(available);
  };

  // Handle continue to destination
  const handleContinueToDestination = () => {
    if (relocationType === "inventory" && (!selectedInventory || quantity <= 0))
      return;
    setCurrentStep("scan-destination");
  };

  // Execute inventory relocation
  const relocateMutation = useMutation({
    mutationFn: async () => {
      if (
        !selectedInventory ||
        !sourcePosition ||
        !destinationPosition ||
        !profileId
      ) {
        throw new Error("Datos incompletos");
      }

      const now = new Date().toISOString();

      const newSourceCount = selectedInventory.box_count - quantity;

      if (newSourceCount <= 0) {
        await supabase
          .from("wms_inventory")
          .delete()
          .eq("id", selectedInventory.id);
      } else {
        await supabase
          .from("wms_inventory")
          .update({ box_count: newSourceCount, updated_at: now })
          .eq("id", selectedInventory.id);
      }

      const { data: existingDestInv } = await supabase
        .from("wms_inventory")
        .select("*")
        .eq("position_id", destinationPosition.id)
        .eq("product_id", selectedInventory.product_id)
        .eq("production_batch_id", selectedInventory.production_batch_id)
        .maybeSingle();

      if (existingDestInv) {
        await supabase
          .from("wms_inventory")
          .update({
            box_count: existingDestInv.box_count + quantity,
            updated_at: now,
          })
          .eq("id", existingDestInv.id);
      } else {
        await supabase.from("wms_inventory").insert({
          position_id: destinationPosition.id,
          product_id: selectedInventory.product_id,
          production_batch_id: selectedInventory.production_batch_id,
          lot_number: selectedInventory.lot_number,
          box_count: quantity,
          reserved_boxes: 0,
          expiration_date: selectedInventory.expiration_date,
          received_at: now,
        });
      }

      await supabase.from("wms_movements").insert({
        movement_type: "transfer",
        product_id: selectedInventory.product_id,
        lot_number: selectedInventory.lot_number,
        box_quantity: quantity,
        from_position_id: sourcePosition.id,
        to_position_id: destinationPosition.id,
        reference_type: "manual",
        performed_by: profileId,
        performed_at: now,
        notes: `Reubicación de ${sourcePosition.full_code} a ${destinationPosition.full_code}`,
      });

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["position-inventory"] });
      queryClient.invalidateQueries({ queryKey: ["warehouse-inventory"] });
      setCurrentStep("completed");
      toast({
        title: "Movimiento completado",
        description: "El inventario ha sido reubicado",
      });
    },
    onError: (err) => {
      toast({
        title: "Error",
        description:
          err instanceof Error
            ? err.message
            : "No se pudo realizar el movimiento",
        variant: "destructive",
      });
    },
  });

  // Execute pallet relocation
  const handleConfirmPalletRelocation = () => {
    if (!pallet || !destinationPosition || !profileId) return;

    relocatePallet.mutate(
      {
        palletId: pallet.id,
        toPositionId: destinationPosition.id,
        userId: profileId,
      },
      {
        onSuccess: (result) => {
          if (result.success) {
            setCurrentStep("completed");
            toast({
              title: "Reubicación completada",
              description: "El pallet ha sido reubicado correctamente",
            });
          } else {
            toast({
              title: "Error",
              description: result.error || "No se pudo reubicar el pallet",
              variant: "destructive",
            });
          }
        },
        onError: (err) => {
          toast({
            title: "Error",
            description:
              err instanceof Error ? err.message : "Error al reubicar",
            variant: "destructive",
          });
        },
      }
    );
  };

  // Key handlers for barcode scanner input
  const handleSourceKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && sourceInput.trim()) {
      handlePositionScan(sourceInput.trim(), "source");
      setSourceInput("");
    }
  };

  const handleDestKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && destInput.trim()) {
      if (relocationType === "inventory") {
        handlePositionScan(destInput.trim(), "destination");
        setDestInput("");
      } else {
        handlePalletDestinationSubmit();
      }
    }
  };

  const handlePositionSelect = (position: Position) => {
    if (currentStep === "scan-destination") {
      const excludeId =
        relocationType === "inventory"
          ? sourcePosition?.id
          : pallet?.position_id;
      if (position.id === excludeId) {
        toast({
          title: "Posición inválida",
          description: "La posición destino debe ser diferente a la origen",
          variant: "destructive",
        });
        return;
      }
      setDestinationPosition(position);
      setCurrentStep("confirm");
    }
  };

  // Handle back navigation
  const handleBack = () => {
    switch (currentStep) {
      case "select-type":
        navigate("/wms");
        break;
      case "scan-source":
      case "scan-pallet":
        resetFlow();
        break;
      case "select-inventory":
        setCurrentStep("scan-source");
        setSourcePosition(null);
        break;
      case "view-pallet":
        setCurrentStep("scan-pallet");
        setPallet(null);
        setPalletSourcePosition(null);
        break;
      case "scan-destination":
        if (relocationType === "inventory") {
          setCurrentStep("select-inventory");
        } else {
          setCurrentStep("view-pallet");
        }
        setDestinationPosition(null);
        break;
      case "confirm":
        setCurrentStep("scan-destination");
        setDestinationPosition(null);
        break;
      default:
        resetFlow();
    }
  };

  // Get step label for header subtitle
  const getStepLabel = () => {
    if (currentStep === "select-type") return "Selecciona tipo de reubicación";

    if (relocationType === "inventory") {
      switch (currentStep) {
        case "scan-source":
          return "Paso 1 de 4 - Escanear posición origen";
        case "select-inventory":
          return "Paso 2 de 4 - Seleccionar inventario";
        case "scan-destination":
          return "Paso 3 de 4 - Escanear posición destino";
        case "confirm":
          return "Paso 4 de 4 - Confirmar movimiento";
        default:
          return "Movimiento completado";
      }
    }

    if (relocationType === "pallet") {
      switch (currentStep) {
        case "scan-pallet":
          return "Paso 1 de 4 - Escanear pallet";
        case "view-pallet":
          return "Paso 2 de 4 - Verificar pallet";
        case "scan-destination":
          return "Paso 3 de 4 - Escanear destino";
        case "confirm":
          return "Paso 4 de 4 - Confirmar reubicación";
        default:
          return "Reubicación completada";
      }
    }

    return "";
  };

  const isLoading =
    isProcessing ||
    lookupPallet.isPending ||
    lookupPosition.isPending ||
    relocatePallet.isPending ||
    relocateMutation.isPending;

  // Render steps
  const renderStep = () => {
    switch (currentStep) {
      case "select-type":
        return <SelectTypeStep onSelect={handleTypeSelect} />;

      case "scan-source":
        return (
          <ScanSourceStep
            inputRef={sourceInputRef}
            value={sourceInput}
            onChange={setSourceInput}
            onKeyPress={handleSourceKeyPress}
            onSubmit={() => {
              if (sourceInput.trim()) {
                handlePositionScan(sourceInput.trim(), "source");
                setSourceInput("");
              }
            }}
            isLoading={isProcessing}
          />
        );

      case "scan-pallet":
        return (
          <ScanPalletStep
            inputRef={palletInputRef}
            value={palletInput}
            onChange={setPalletInput}
            onKeyPress={(e) => e.key === "Enter" && handlePalletSubmit()}
            onSubmit={handlePalletSubmit}
            isLoading={lookupPallet.isPending}
          />
        );

      case "select-inventory":
        return (
          <SelectInventoryStep
            position={sourcePosition!}
            inventory={sourceInventory || []}
            selectedInventory={selectedInventory}
            quantity={quantity}
            onInventorySelect={handleInventorySelect}
            onQuantityChange={setQuantity}
            onContinue={handleContinueToDestination}
            onBack={handleBack}
            isLoading={loadingInventory}
          />
        );

      case "view-pallet":
        return (
          <ViewPalletStep
            pallet={pallet!}
            sourcePosition={palletSourcePosition}
            onContinue={handleContinueToDestination}
            onBack={handleBack}
          />
        );

      case "scan-destination":
        if (relocationType === "inventory") {
          return (
            <ScanDestinationStep
              sourcePosition={sourcePosition!}
              selectedInventory={selectedInventory!}
              quantity={quantity}
              suggestedPositions={suggestedPositions || []}
              inputRef={destInputRef}
              value={destInput}
              onChange={setDestInput}
              onKeyPress={handleDestKeyPress}
              onSubmit={() => {
                if (destInput.trim()) {
                  handlePositionScan(destInput.trim(), "destination");
                  setDestInput("");
                }
              }}
              onPositionSelect={handlePositionSelect}
              onBack={handleBack}
              isLoading={isProcessing}
            />
          );
        } else {
          return (
            <ScanPalletDestinationStep
              pallet={pallet!}
              sourcePosition={palletSourcePosition}
              suggestedPositions={suggestedPositions || []}
              inputRef={destInputRef}
              value={destInput}
              onChange={setDestInput}
              onKeyPress={handleDestKeyPress}
              onSubmit={handlePalletDestinationSubmit}
              onPositionSelect={handlePositionSelect}
              onBack={handleBack}
              isLoading={lookupPosition.isPending}
            />
          );
        }

      case "confirm":
        if (relocationType === "inventory") {
          return (
            <ConfirmInventoryStep
              sourcePosition={sourcePosition!}
              destinationPosition={destinationPosition!}
              selectedInventory={selectedInventory!}
              quantity={quantity}
              onConfirm={() => relocateMutation.mutate()}
              onBack={handleBack}
              isLoading={relocateMutation.isPending}
            />
          );
        } else {
          return (
            <ConfirmPalletStep
              pallet={pallet!}
              sourcePosition={palletSourcePosition}
              destinationPosition={destinationPosition!}
              onConfirm={handleConfirmPalletRelocation}
              onBack={handleBack}
              isLoading={relocatePallet.isPending}
            />
          );
        }

      case "completed":
        if (relocationType === "inventory") {
          return (
            <CompletedInventoryStep
              sourcePosition={sourcePosition!}
              destinationPosition={destinationPosition!}
              selectedInventory={selectedInventory!}
              quantity={quantity}
              onNewRelocation={resetFlow}
              onBackToMenu={() => navigate("/wms")}
            />
          );
        } else {
          return (
            <CompletedPalletStep
              pallet={pallet!}
              sourcePosition={palletSourcePosition}
              destinationPosition={destinationPosition!}
              onNewRelocation={resetFlow}
              onBackToMenu={() => navigate("/wms/pallets")}
            />
          );
        }
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Reubicación"
        description={getStepLabel()}
        back={
          currentStep !== "completed" && (
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 h-9 text-muted-foreground"
              onClick={handleBack}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver
            </Button>
          )
        }
        actions={
          relocationType &&
          currentStep !== "completed" && (
            <Badge variant="outline" className="text-sm">
              {relocationType === "inventory" ? "Inventario" : "Pallet"}
            </Badge>
          )
        }
      />

      <div className="mx-auto max-w-2xl">{renderStep()}</div>
    </PageContainer>
  );
}

// Step 0: Select Type
function SelectTypeStep({
  onSelect,
}: {
  onSelect: (type: RelocationType) => void;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold">¿Qué deseas reubicar?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Selecciona el tipo de reubicación
        </p>
      </div>

      <div className="grid gap-4">
        <Card
          className="cursor-pointer transition-all hover:border-primary hover:bg-primary/5"
          onClick={() => onSelect("inventory")}
        >
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-blue-100 p-3">
              <Boxes className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold">Inventario (Cajas)</h3>
              <p className="text-sm text-muted-foreground">
                Mover cajas específicas de una posición a otra
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer transition-all hover:border-primary hover:bg-primary/5"
          onClick={() => onSelect("pallet")}
        >
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-orange-100 p-3">
              <Package className="h-8 w-8 text-orange-600" />
            </div>
            <div>
              <h3 className="font-semibold">Pallet (Completo)</h3>
              <p className="text-sm text-muted-foreground">
                Reubicar un pallet completo a otra posición
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Step 1 (Inventory): Scan Source
function ScanSourceStep({
  inputRef,
  value,
  onChange,
  onKeyPress,
  onSubmit,
  isLoading,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSubmit: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold">Escanear Posición Origen</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Escanea o ingresa el código de la posición origen
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="relative">
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyPress={onKeyPress}
                placeholder="Ej: BOD-001-HA-R01-L1-P01"
                className="pr-10 text-center font-mono text-lg"
                disabled={isLoading}
                autoFocus
              />
              {value && (
                <button
                  type="button"
                  onClick={() => onChange("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              className="w-full"
              onClick={onSubmit}
              disabled={!value.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                "Buscar Posición"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Step 1 (Pallet): Scan Pallet
function ScanPalletStep({
  inputRef,
  value,
  onChange,
  onKeyPress,
  onSubmit,
  isLoading,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSubmit: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center justify-center py-8">
        <Package className="mb-4 h-16 w-16 text-muted-foreground/50" />
        <h2 className="text-lg font-medium">
          Escanea o ingresa el código del pallet
        </h2>
        <p className="text-sm text-muted-foreground">
          Usa el lector de código de barras o escribe el código manualmente
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <Input
              ref={inputRef}
              placeholder="Código o barcode del pallet..."
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyPress}
              disabled={isLoading}
              className="text-center font-mono text-lg"
            />
            <Button
              className="w-full"
              onClick={onSubmit}
              disabled={!value.trim() || isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Package className="mr-2 h-4 w-4" />
              )}
              Buscar Pallet
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Step 2 (Inventory): Select Inventory
function SelectInventoryStep({
  position,
  inventory,
  selectedInventory,
  quantity,
  onInventorySelect,
  onQuantityChange,
  onContinue,
  onBack,
  isLoading,
}: {
  position: Position;
  inventory: InventoryWithProduct[];
  selectedInventory: InventoryWithProduct | null;
  quantity: number;
  onInventorySelect: (inv: InventoryWithProduct) => void;
  onQuantityChange: (qty: number) => void;
  onContinue: () => void;
  onBack: () => void;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const maxQuantity = selectedInventory
    ? selectedInventory.box_count - (selectedInventory.reserved_boxes || 0)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <Badge variant="secondary" className="mb-2">
          {position.full_code}
        </Badge>
        <h2 className="text-lg font-semibold">Seleccionar Inventario</h2>
      </div>

      {inventory.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-8">
            <Package className="h-10 w-10 text-gray-300" />
            <p className="text-center text-gray-500">
              No hay inventario en esta posición
            </p>
          </CardContent>
        </Card>
      ) : (
        <RadioGroup
          value={selectedInventory?.id || ""}
          onValueChange={(id) => {
            const inv = inventory.find((i) => i.id === id);
            if (inv) onInventorySelect(inv);
          }}
        >
          <div className="space-y-3">
            {inventory.map((inv) => {
              const available = inv.box_count - (inv.reserved_boxes || 0);
              const expiryDate = inv.expiration_date
                ? dayjs(inv.expiration_date).format("DD/MM/YYYY")
                : null;

              return (
                <Label
                  key={inv.id}
                  htmlFor={inv.id}
                  className="block cursor-pointer"
                >
                  <Card
                    className={`transition-all ${
                      selectedInventory?.id === inv.id
                        ? "border-blue-500 bg-blue-50"
                        : "hover:border-gray-300"
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <RadioGroupItem
                          value={inv.id}
                          id={inv.id}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="font-medium">
                            {inv.product?.short_name || "Producto"}
                          </p>
                          {inv.production_batch?.lot_number && (
                            <p className="text-xs text-gray-500">
                              Lote: {inv.production_batch.lot_number}
                            </p>
                          )}
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-500">
                            <span>{available} cajas disponibles</span>
                            {expiryDate && <span>Vence: {expiryDate}</span>}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Label>
              );
            })}
          </div>
        </RadioGroup>
      )}

      {selectedInventory && (
        <div>
          <label className="text-sm font-medium text-gray-700">
            Cantidad a mover
          </label>
          <div className="mt-2 flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => onQuantityChange(Math.max(1, quantity - 1))}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-20 text-center text-2xl font-bold">
              {quantity}
            </span>
            <Button
              variant="outline"
              size="icon"
              onClick={() =>
                onQuantityChange(Math.min(maxQuantity, quantity + 1))
              }
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-1 text-center text-xs text-gray-500">
            Máximo: {maxQuantity} cajas
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <Button variant="outline" className="flex-1" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Cambiar posición
        </Button>
        <Button
          className="flex-1"
          onClick={onContinue}
          disabled={!selectedInventory || quantity <= 0}
        >
          Continuar
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// Step 2 (Pallet): View Pallet Details
function ViewPalletStep({
  pallet,
  sourcePosition,
  onContinue,
  onBack,
}: {
  pallet: PalletWithDetails;
  sourcePosition: Position | null | undefined;
  onContinue: () => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="font-mono">{pallet.code}</span>
            <Badge>{PALLET_STATUS_LABELS[pallet.status]}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {pallet.pallet_type === "mixed" && (
            <Badge variant="default" className="bg-purple-100 text-purple-700">
              {PALLET_TYPE_LABELS[pallet.pallet_type]}
            </Badge>
          )}

          <div className="rounded-lg bg-muted p-4">
            <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" />
              Ubicación Actual
            </div>
            {sourcePosition ? (
              <p className="font-mono text-xl font-bold">
                {(sourcePosition as any).full_code || sourcePosition.code}
              </p>
            ) : (
              <p className="text-muted-foreground">Sin ubicación asignada</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg border p-3 text-center">
              <Boxes className="mx-auto mb-1 h-5 w-5 text-muted-foreground" />
              <p className="text-2xl font-bold">{pallet.current_box_count}</p>
              <p className="text-xs text-muted-foreground">Cajas</p>
            </div>
            <div className="rounded-lg border p-3 text-center">
              <Calendar className="mx-auto mb-1 h-5 w-5 text-muted-foreground" />
              <p className="text-lg font-bold">
                {pallet.earliest_expiration || pallet.expiration_date
                  ? dayjs(
                      pallet.earliest_expiration || pallet.expiration_date
                    ).format("DD/MM/YY")
                  : "-"}
              </p>
              <p className="text-xs text-muted-foreground">Vencimiento</p>
            </div>
          </div>

          {pallet.batches && pallet.batches.length > 0 && (
            <div>
              <p className="mb-2 text-sm text-muted-foreground">
                Contenido ({pallet.batches.length} lote
                {pallet.batches.length > 1 ? "s" : ""})
              </p>
              <div className="max-h-40 space-y-2 overflow-y-auto">
                {pallet.batches.map((batch) => (
                  <div
                    key={batch.id}
                    className="flex items-center justify-between rounded border p-2 text-sm"
                  >
                    <span>{batch.product?.short_name || "-"}</span>
                    <span className="font-medium">{batch.box_count} cajas</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Button className="w-full" onClick={onContinue}>
        <ArrowRight className="mr-2 h-4 w-4" />
        Continuar
      </Button>
    </div>
  );
}

// Step 3 (Inventory): Scan Destination
function ScanDestinationStep({
  sourcePosition,
  selectedInventory,
  quantity,
  suggestedPositions,
  inputRef,
  value,
  onChange,
  onKeyPress,
  onSubmit,
  onPositionSelect,
  onBack,
  isLoading,
}: {
  sourcePosition: Position;
  selectedInventory: InventoryWithProduct;
  quantity: number;
  suggestedPositions: Position[];
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSubmit: () => void;
  onPositionSelect: (pos: Position) => void;
  onBack: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold">Escanear Posición Destino</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Escanea o ingresa el código de la posición destino
        </p>
      </div>

      <Card className="border-blue-200 bg-blue-50">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <Package className="h-8 w-8 text-blue-500" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {selectedInventory.product?.short_name}
              </p>
              <p className="text-xs text-gray-600">
                Desde: {sourcePosition.full_code}
              </p>
            </div>
            <Badge variant="secondary">{quantity} cajas</Badge>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="relative">
              <Input
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyPress={onKeyPress}
                placeholder="Ej: BOD-001-HA-R01-L1-P01"
                className="pr-10 text-center font-mono text-lg"
                disabled={isLoading}
                autoFocus
              />
              {value && (
                <button
                  type="button"
                  onClick={() => onChange("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <Button
              className="w-full"
              onClick={onSubmit}
              disabled={!value.trim() || isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Buscando...
                </>
              ) : (
                "Buscar Posición"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {suggestedPositions.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-600">
            Posiciones sugeridas:
          </p>
          <div className="space-y-2">
            {suggestedPositions.map((pos) => (
              <Button
                key={pos.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => onPositionSelect(pos)}
                disabled={isLoading}
              >
                <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                {pos.full_code}
              </Button>
            ))}
          </div>
        </div>
      )}

      <Button
        variant="ghost"
        className="w-full"
        onClick={onBack}
        disabled={isLoading}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver
      </Button>
    </div>
  );
}

// Step 3 (Pallet): Scan Destination
function ScanPalletDestinationStep({
  pallet,
  sourcePosition,
  suggestedPositions,
  inputRef,
  value,
  onChange,
  onKeyPress,
  onSubmit,
  onPositionSelect,
  onBack,
  isLoading,
}: {
  pallet: PalletWithDetails;
  sourcePosition: Position | null | undefined;
  suggestedPositions: Position[];
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (value: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onSubmit: () => void;
  onPositionSelect: (pos: Position) => void;
  onBack: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      <Card className="border-orange-200 bg-orange-50">
        <CardContent className="p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Pallet:</span>
            <span className="font-mono font-medium">{pallet?.code}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Desde:</span>
            <span className="font-mono font-medium">
              {sourcePosition
                ? (sourcePosition as any).full_code || sourcePosition.code
                : "Sin ubicación"}
            </span>
          </div>
        </CardContent>
      </Card>

      <Separator />

      <div className="flex flex-col items-center justify-center py-6">
        <MapPin className="mb-4 h-16 w-16 text-muted-foreground/50" />
        <h2 className="text-lg font-medium">Escanea la posición destino</h2>
        <p className="text-sm text-muted-foreground">
          Escanea el código de la ubicación donde deseas colocar el pallet
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <Input
              ref={inputRef}
              placeholder="Código de posición destino..."
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyPress}
              disabled={isLoading}
              className="text-center font-mono text-lg"
            />
            <Button
              className="w-full"
              onClick={onSubmit}
              disabled={!value.trim() || isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <MapPin className="mr-2 h-4 w-4" />
              )}
              Verificar Destino
            </Button>
          </div>
        </CardContent>
      </Card>

      {suggestedPositions.length > 0 && (
        <div>
          <p className="mb-2 text-sm font-medium text-gray-600">
            Posiciones sugeridas:
          </p>
          <div className="space-y-2">
            {suggestedPositions.map((pos) => (
              <Button
                key={pos.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => onPositionSelect(pos)}
                disabled={isLoading}
              >
                <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                {pos.full_code}
              </Button>
            ))}
          </div>
        </div>
      )}

      <Button
        variant="ghost"
        className="w-full"
        onClick={onBack}
        disabled={isLoading}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Volver
      </Button>
    </div>
  );
}

// Step 4 (Inventory): Confirm
function ConfirmInventoryStep({
  sourcePosition,
  destinationPosition,
  selectedInventory,
  quantity,
  onConfirm,
  onBack,
  isLoading,
}: {
  sourcePosition: Position;
  destinationPosition: Position;
  selectedInventory: InventoryWithProduct;
  quantity: number;
  onConfirm: () => void;
  onBack: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold">Confirmar Movimiento</h2>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div>
            <span className="text-xs text-gray-500">Producto</span>
            <p className="font-medium">
              {selectedInventory.product?.short_name}
            </p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Cantidad</span>
            <p className="font-medium">{quantity} cajas</p>
          </div>
          {selectedInventory.production_batch?.lot_number && (
            <div>
              <span className="text-xs text-gray-500">Lote</span>
              <p className="font-mono font-medium">
                {selectedInventory.production_batch.lot_number}
              </p>
            </div>
          )}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs text-gray-500">Desde</span>
                <p className="font-mono font-medium">
                  {sourcePosition.full_code}
                </p>
              </div>
              <ArrowRight className="h-5 w-5 text-gray-400" />
              <div className="text-right">
                <span className="text-xs text-gray-500">Hacia</span>
                <p className="font-mono font-medium">
                  {destinationPosition.full_code}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Button
        className="w-full"
        size="lg"
        onClick={onConfirm}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        ) : (
          <Check className="mr-2 h-5 w-5" />
        )}
        Confirmar Movimiento
      </Button>

      <Button
        variant="ghost"
        className="w-full"
        onClick={onBack}
        disabled={isLoading}
      >
        Cancelar
      </Button>
    </div>
  );
}

// Step 4 (Pallet): Confirm
function ConfirmPalletStep({
  pallet,
  sourcePosition,
  destinationPosition,
  onConfirm,
  onBack,
  isLoading,
}: {
  pallet: PalletWithDetails;
  sourcePosition: Position | null | undefined;
  destinationPosition: Position;
  onConfirm: () => void;
  onBack: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Confirmar Reubicación</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
            <div className="rounded-lg border p-4 text-center">
              <p className="mb-1 text-xs text-muted-foreground">Desde</p>
              <p className="font-mono text-lg font-bold">
                {sourcePosition
                  ? (sourcePosition as any).full_code || sourcePosition.code
                  : "Sin ubicación"}
              </p>
            </div>

            <ArrowRight className="h-6 w-6 text-muted-foreground" />

            <div className="rounded-lg border-2 border-primary bg-primary/5 p-4 text-center">
              <p className="mb-1 text-xs text-muted-foreground">Hacia</p>
              <p className="font-mono text-lg font-bold text-primary">
                {(destinationPosition as any).full_code ||
                  destinationPosition.code}
              </p>
            </div>
          </div>

          <Separator />

          <div className="text-center text-sm text-muted-foreground">
            <p>
              El pallet{" "}
              <span className="font-mono font-medium">{pallet?.code}</span> con{" "}
              <span className="font-medium">
                {pallet?.current_box_count} cajas
              </span>{" "}
              será reubicado
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4">
        <Button variant="outline" onClick={onBack}>
          <X className="mr-2 h-4 w-4" />
          Cancelar
        </Button>
        <Button onClick={onConfirm} disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Check className="mr-2 h-4 w-4" />
          )}
          Confirmar
        </Button>
      </div>
    </div>
  );
}

// Completed Step (Inventory)
function CompletedInventoryStep({
  sourcePosition,
  destinationPosition,
  selectedInventory,
  quantity,
  onNewRelocation,
  onBackToMenu,
}: {
  sourcePosition: Position;
  destinationPosition: Position;
  selectedInventory: InventoryWithProduct;
  quantity: number;
  onNewRelocation: () => void;
  onBackToMenu: () => void;
}) {
  return (
    <div className="space-y-6 py-8">
      <div className="text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
          <Check className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-xl font-semibold">Movimiento Completado</h2>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <p className="text-sm font-medium">
                {selectedInventory.product?.short_name}
              </p>
              <p className="text-xs text-gray-500">{quantity} cajas</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-center gap-4 text-sm">
            <span className="font-mono">{sourcePosition.full_code}</span>
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <span className="font-mono">{destinationPosition.full_code}</span>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <Button className="w-full" onClick={onNewRelocation}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Nueva Reubicación
        </Button>
        <Button variant="outline" className="w-full" onClick={onBackToMenu}>
          Volver al Menú
        </Button>
      </div>
    </div>
  );
}

// Completed Step (Pallet)
function CompletedPalletStep({
  pallet,
  sourcePosition,
  destinationPosition,
  onNewRelocation,
  onBackToMenu,
}: {
  pallet: PalletWithDetails;
  sourcePosition: Position | null | undefined;
  destinationPosition: Position;
  onNewRelocation: () => void;
  onBackToMenu: () => void;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="mb-4 rounded-full bg-green-100 p-4">
            <Check className="h-12 w-12 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-green-700">
            Reubicación Exitosa
          </h2>
          <p className="mt-2 text-muted-foreground">
            El pallet{" "}
            <span className="font-mono font-medium">{pallet?.code}</span> ha
            sido reubicado correctamente
          </p>

          <div className="mt-6 grid w-full max-w-sm grid-cols-[1fr_auto_1fr] items-center gap-4">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Desde</p>
              <p className="font-mono text-sm">
                {sourcePosition
                  ? (sourcePosition as any).full_code || sourcePosition.code
                  : "Sin ubicación"}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-green-600" />
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Hacia</p>
              <p className="font-mono text-sm font-medium text-green-700">
                {(destinationPosition as any).full_code ||
                  destinationPosition.code}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4">
          <Button variant="outline" onClick={onBackToMenu}>
            Ver Pallets
          </Button>
          <Button onClick={onNewRelocation}>
            <Package className="mr-2 h-4 w-4" />
            Reubicar Otro
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default RelocationPage;
